import { useEffect, useState } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import MapIcon from '@mui/icons-material/Map';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import first from 'lodash/first';

import { Property } from '../../../Graphql/Generated/graphql';
import { useAppDispatch, useAppSelector } from '../../../Hooks/useApp';
import {
  resetListings,
  searchListings,
  selectListings,
  selectSearchCity,
  useLazyGetPropertiesFreeformQuery,
} from '../../../State/slices/listing';
import { LISTINGS_META_DESCRIPTION, keywords } from '../../../Types/MetaData';
import Map from '../../../legacy/Components/Map';
import { Loading } from '../../components/loading/Loading';
import { useIsMobile } from '../../hooks/useIsMobile';
import { PropertyCard } from './components/PropertyCard';

export const CardGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fill, 300px);
  gap: 20px;
  padding: 20px;
  justify-content: center;
`;

const ButtonAlignment = styled('div')`
  position: fixed;
  top: 85%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
`;

const Container = styled('form')<{ $showMap: boolean }>`
  display: grid;
  grid-template-columns: ${({ $showMap }) => ($showMap ? '1fr 1fr' : '1fr')};
  gap: 20px;
  overflow-x: hidden;
  transition: grid-template-columns 1s ease-in-out;

  @media (min-width: 1250px) {
    grid-template-columns: ${({ $showMap }) => ($showMap ? '70% 30%' : '100%')};
  }
`;

// TODO:
const MapContainer = styled('div')<{ $showMap: boolean }>`
  height: 100%;
  transition: transform 300ms ease-in-out;
  &.slide-enter {
    transform: translateX(100%);
  }
  &.slide-enter-active {
    transform: translateX(0);
  }
  &.slide-exit {
    transform: translateX(0);
  }
  &.slide-exit-active {
    transform: translateX(-100%);
  }
`;

const MobileCardGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fill, 354px);
  gap: 20px;
  padding: 20px;
  justify-content: center;
`;

const PAGE_SIZE = 15;

async function getListings(city: string, getPropertiesFreeform: any, page = 0) {
  // TODO: Rework state... Do state local state management...
  const properties = {
    searchTerm: city,
    searchStart: '',
    searchEnd: '',
    pagination: {
      paginate: true,
      page,
      pageSize: PAGE_SIZE,
    },
  };

  await getPropertiesFreeform(properties);
}

export const ListingsPage = () => {
  // TODO: Display prices of each property in map in respective locations...
  // TODO: Clicking on the price will show the card over the map with it's details and a link to the property page

  const [showMap, setShowMap] = useState(true);
  const searchCity = useAppSelector(selectSearchCity);
  const [totalPages, setTotalPages] = useState(0);
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const [getPropertiesFreeform, { data, isLoading }] = useLazyGetPropertiesFreeformQuery();
  const [page, setPage] = useState(0);
  // Initial listings page load
  useEffect(() => {
    getListings(searchCity, getPropertiesFreeform, page);
  }, []);

  // This is for the infinite scroll and when a search is performed
  useEffect(() => {
    getListings(searchCity, getPropertiesFreeform, page);
  }, [getPropertiesFreeform, page, searchCity]);

  // TODO: If the location.state changes then a search was performed on a different city
  useEffect(() => {
    dispatch(resetListings());
    setPage(0);
  }, [searchCity]);

  useEffect(() => {
    dispatch(searchListings(data?.getFreeformSearch || []));
  }, [data, isLoading, dispatch]);

  const properties = useAppSelector(selectListings);

  useEffect(() => {
    const propertyCount = first(properties)?.propertyCount || 0;
    if (propertyCount > 0) setTotalPages(Math.round(propertyCount / PAGE_SIZE));
  }, [properties]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Listings - FLXlease</title>
          <meta name='description' content={LISTINGS_META_DESCRIPTION} />
          <meta name='keywords' content={keywords} />
          <link rel='canonical' href={`https://flxlease.com/`} />
        </Helmet>
      </HelmetProvider>
      {isMobile ? (
        <InfiniteScroll
          dataLength={properties?.length}
          next={() =>
            setPage((prev: number) => {
              if (prev < totalPages) return prev + 1;
              return prev;
            })
          }
          scrollableTarget='mobile-scroll'
          hasMore={true}
          loader={<Loading isLoading={isLoading} />}
        >
          <MobileCardGrid>
            {properties.map((property) => (
              <PropertyCard key={property.id} property={property} width={354} />
            ))}
          </MobileCardGrid>
        </InfiniteScroll>
      ) : (
        <>
          <ButtonAlignment>
            <Button
              variant='contained'
              onClick={() => setShowMap((prev) => !prev)}
              endIcon={<MapIcon />}
              sx={{ borderRadius: '15px' }}
            >
              {showMap ? 'Hide Map' : 'Show Map'}
            </Button>
          </ButtonAlignment>
          <Container $showMap={showMap}>
            <InfiniteScroll
              dataLength={properties?.length}
              next={() =>
                setPage((prev: number) => {
                  if (prev < totalPages) return prev + 1;
                  return prev;
                })
              }
              hasMore={true}
              loader={<Loading isLoading={isLoading} />}
            >
              <CardGrid>
                {properties?.map((property: Property) => (
                  <PropertyCard key={property.id} property={property} />
                ))}
              </CardGrid>
            </InfiniteScroll>
            <CSSTransition in={showMap} timeout={300} classNames='slide' unmountOnExit>
              <MapContainer $showMap={showMap}>
                <Map
                  properties={properties}
                  height={showMap ? '100vh' : '0%'}
                  width='100vh'
                  position='fixed'
                />
              </MapContainer>
            </CSSTransition>
          </Container>
        </>
      )}
    </>
  );
};

export default ListingsPage;
